import React, { useMemo, useCallback, useRef, useReducer, useState } from "react"
import {
  LoaderCircleIcon,
  SaveIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XCircleIcon,
} from "lucide-react"

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"

import { fetchAPI, handleResponse } from "@/utils"
import type { EnumerationItem, AutoTag, UserTag } from "@/utils/types"

export interface ExtendedEvidenceCount {
  name: string
  display?: string
  criteriaLabel?: string
  color?: string
  background?: string
  showName?: boolean
  showValue?: boolean
  iconType?: string
  icon?: string
}

interface GeneralSettings {
  foldersViewEnabled: boolean
  showSearch: boolean
  summariserEnabled: boolean
}

type TagConfigType = "brand-lenses" | "brand-ui-config"

export interface RecordListState {
  userTags: UserTag[]
  evidenceCounts: ExtendedEvidenceCount[]
  autoTags: AutoTag[]
  foldersViewEnabled: boolean
  showSearch: boolean
  summariserEnabled: boolean
}

export interface SharedRecordListTabProps {
  initialConfig: RecordListState
  onSave: (state: RecordListState) => void
  onRemove: () => void
  isFetching: boolean
  currentAction?: "SAVE_SECTION" | "REMOVE_SECTION" | null
  actionResult?: { success?: boolean; action?: string }
  tagConfigType: TagConfigType
  data: {
    loadedOrganisation: string
    loadedProject?: string
    loadedBrand?: string
    evidenceCountData?: Array<{ name: string; criteriaLabel?: string }>
    userTagTypes?: string[]
    autoTagTypes: string[]
  }
}

const displayOptions = [
  { value: "show", label: "Show" },
  { value: "hide", label: "Hide" },
  { value: "none", label: "None" },
] as const

export type ListAction<T> =
  | { type: "ADD"; item: T }
  | { type: "REMOVE"; index: number }
  | { type: "UPDATE"; index: number; item: T }
  | { type: "UPDATE_PROPERTY"; index: number; property: keyof T; value: T[keyof T] }
  | { type: "SET"; items: T[] }

export function createListReducer<T>() {
  return function listReducer(state: T[], action: ListAction<T>): T[] {
    switch (action.type) {
      case "ADD":
        return [action.item, ...state]
      case "REMOVE":
        return state.filter((_, i) => i !== action.index)
      case "UPDATE":
        return state.map((item, i) => (i === action.index ? action.item : item))
      case "UPDATE_PROPERTY":
        return state.map((item, i) => (i === action.index ? { ...item, [action.property]: action.value } : item))
      case "SET":
        return action.items
      default:
        return state
    }
  }
}

const userTagReducer = createListReducer<UserTag>()
const evidenceCountReducer = createListReducer<ExtendedEvidenceCount>()
const autoTagReducer = createListReducer<AutoTag>()
const enumerationItemReducer = createListReducer<EnumerationItem>()

interface TagValuesSource {
  loadedOrganisation: string
  loadedProject?: string
  loadedBrand?: string
}

function useTagValues(source: TagValuesSource) {
  const [tagValuesForType, setTagValuesForType] = useState<Array<{ value: string; name?: string }>>([])
  const [loadingTagValues, setLoadingTagValues] = useState(false)

  const loadTagValues = useCallback(
    async (tagType: string) => {
      if (tagType.length === 0) {
        setTagValuesForType([])
        return
      }

      setLoadingTagValues(true)
      try {
        const urlPath = source.loadedProject
          ? `/api/unityviewer/tag/${encodeURIComponent(source.loadedOrganisation)}/${encodeURIComponent(source.loadedProject)}/${encodeURIComponent(tagType)}`
          : `/api/unityviewer/tag/${encodeURIComponent(source.loadedOrganisation)}/${encodeURIComponent(source.loadedBrand ?? "")}/${encodeURIComponent(tagType)}`

        const details = await fetchAPI<Array<{ value?: string; name?: string }>>(urlPath).then(handleResponse)

        const values = details
          .map((item) => ({ value: item.name ?? item.value ?? "", name: item.name }))
          .filter((item) => item.value !== "")

        setTagValuesForType(values)
      } catch (error) {
        console.error("Error loading tag values:", error)
        setTagValuesForType([])
      } finally {
        setLoadingTagValues(false)
      }
    },
    [source.loadedOrganisation, source.loadedProject, source.loadedBrand],
  )

  const resetTagValues = useCallback(() => {
    setTagValuesForType([])
  }, [])

  return { tagValuesForType, loadingTagValues, loadTagValues, resetTagValues }
}

const AddEvidenceCountDialog = React.memo(
  ({
    data,
    onAdd,
    existingCounts,
  }: {
    data: SharedRecordListTabProps["data"]
    onAdd: (evidenceCount: ExtendedEvidenceCount) => void
    existingCounts: ExtendedEvidenceCount[]
  }) => {
    const formRef = useRef<HTMLFormElement>(null)

    const availableCounts = useMemo(() => {
      const existingNames = new Set(existingCounts.map((ec) => ec.name))
      return data.evidenceCountData?.filter((ec) => !existingNames.has(ec.name)) ?? []
    }, [data.evidenceCountData, existingCounts])

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (formRef.current == null) return

        const formData = new FormData(formRef.current)
        const selectedName = formData.get("evidenceCountName") as string
        const display = formData.get("display") as string
        const color = formData.get("color") as string
        const background = formData.get("background") as string
        const showName = formData.get("showName") === "on"
        const showValue = formData.get("showValue") === "on"
        const iconType = formData.get("iconType") as string
        const icon = formData.get("icon") as string

        if (selectedName != null) {
          const selectedEvidence = data.evidenceCountData?.find((ec) => ec.name === selectedName)

          onAdd({
            name: selectedName,
            criteriaLabel: selectedEvidence?.criteriaLabel,
            display: display ?? "show",
            color: color ?? undefined,
            background: background ?? undefined,
            showName: showName ?? undefined,
            showValue: showValue ?? undefined,
            iconType: iconType ?? undefined,
            icon: icon ?? undefined,
          })

          formRef.current?.reset()
        }
      },
      [onAdd, data.evidenceCountData],
    )

    if (availableCounts.length === 0) {
      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="cursor-not-allowed border-border bg-muted px-2 text-xs opacity-50 hover:bg-secondary sm:px-3 sm:text-sm dark:border-border dark:bg-muted"
            >
              <PlusIcon className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
              <span className="xs:inline hidden">Add Evidence Count</span>
              <span className="xs:hidden">Add</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent side="top" className="max-w-sm">
            <p className="text-sm">All available evidence counts have been added.</p>
          </PopoverContent>
        </Popover>
      )
    }

    return (
      <Dialog>
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="border-green-200 bg-green-50 px-2 text-xs hover:bg-green-100 sm:px-3 sm:text-sm"
          >
            <PlusIcon className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
            <span className="xs:inline hidden">Add Evidence Count</span>
            <span className="xs:hidden">Add</span>
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Evidence Count</DialogTitle>
          </DialogHeader>
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Evidence Count</label>
                <Select name="evidenceCountName" required>
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue placeholder="Select evidence count..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCounts.map((ec) => (
                      <SelectItem key={ec.name} value={ec.name}>
                        <div className="flex flex-col items-start">
                          <span className="font-medium text-muted-foreground">{ec.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium">Display</label>
                <Select name="display" defaultValue="show">
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {displayOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Text Color</label>
                <input
                  type="color"
                  name="color"
                  defaultValue="#000000"
                  className="mt-1 h-10 w-full cursor-pointer rounded border border-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Background Color</label>
                <input
                  type="color"
                  name="background"
                  defaultValue="#ffffff"
                  className="mt-1 h-10 w-full cursor-pointer rounded border border-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex items-center space-x-2">
                <Checkbox id="showName" name="showName" defaultChecked={true} />
                <label htmlFor="showName" className="text-xs sm:text-sm">
                  Show Name
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="showValue" name="showValue" defaultChecked={true} />
                <label htmlFor="showValue" className="text-xs sm:text-sm">
                  Show Value
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Icon Type</label>
                <Select name="iconType">
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue placeholder="Select icon type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="character">Character</SelectItem>
                    <SelectItem value="svg">SVG</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium">Icon</label>
                <Input name="icon" placeholder="Enter character or SVG path..." className="mt-1" />
              </div>
            </div>

            <div className="flex items-center justify-end gap-4 border-t pt-4">
              <DialogClose asChild>
                <Button type="button" variant="ghost">
                  Cancel
                </Button>
              </DialogClose>
              <DialogClose asChild>
                <Button type="submit">Add Evidence Count</Button>
              </DialogClose>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    )
  },
)

AddEvidenceCountDialog.displayName = "AddEvidenceCountDialog"

const EditEvidenceCountDialog = React.memo(
  ({
    evidenceCount,
    onSave,
    open,
    onOpenChange,
  }: {
    evidenceCount: ExtendedEvidenceCount
    onSave: (updated: ExtendedEvidenceCount) => void
    open: boolean
    onOpenChange: (open: boolean) => void
  }) => {
    const formRef = useRef<HTMLFormElement>(null)

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (formRef.current == null) return

        const formData = new FormData(formRef.current)

        const updated: ExtendedEvidenceCount = {
          ...evidenceCount,
          display: (formData.get("display") as string) ?? "show",
          color: (formData.get("color") as string) ?? undefined,
          background: (formData.get("background") as string) ?? undefined,
          showName: formData.get("showName") === "on",
          showValue: formData.get("showValue") === "on",
          iconType: (formData.get("iconType") as string) ?? undefined,
          icon: (formData.get("icon") as string) ?? undefined,
        }

        onSave(updated)
        onOpenChange(false)
      },
      [evidenceCount, onSave, onOpenChange],
    )

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Evidence Count</DialogTitle>
          </DialogHeader>
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Evidence Count</label>
                <Input value={evidenceCount.name} readOnly className="mt-1 bg-muted" />
              </div>

              <div>
                <label className="text-sm font-medium">Display</label>
                <Select name="display" defaultValue={evidenceCount.display ?? "show"}>
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {displayOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Text Color</label>
                <input
                  type="color"
                  name="color"
                  defaultValue={evidenceCount.color ?? "#000000"}
                  className="mt-1 h-10 w-full cursor-pointer rounded border border-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Background Color</label>
                <input
                  type="color"
                  name="background"
                  defaultValue={evidenceCount.background ?? "#ffffff"}
                  className="mt-1 h-10 w-full cursor-pointer rounded border border-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex items-center space-x-2">
                <Checkbox id="editShowName" name="showName" defaultChecked={evidenceCount.showName !== false} />
                <label htmlFor="editShowName" className="text-xs sm:text-sm">
                  Show Name
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="editShowValue" name="showValue" defaultChecked={evidenceCount.showValue !== false} />
                <label htmlFor="editShowValue" className="text-xs sm:text-sm">
                  Show Value
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Icon Type</label>
                <Select name="iconType" defaultValue={evidenceCount.iconType ?? undefined}>
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue placeholder="Select icon type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="character">Character</SelectItem>
                    <SelectItem value="svg">SVG</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium">Icon</label>
                <Input
                  name="icon"
                  defaultValue={evidenceCount.icon ?? ""}
                  placeholder="Enter character or SVG path..."
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-4 border-t pt-4">
              <DialogClose asChild>
                <Button type="button" variant="ghost">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    )
  },
)

EditEvidenceCountDialog.displayName = "EditEvidenceCountDialog"

const AddAutoTagDialog = React.memo(
  ({
    data,
    onAdd,
    existingAutoTags,
  }: {
    data: SharedRecordListTabProps["data"]
    onAdd: (autoTag: AutoTag) => void
    existingAutoTags: AutoTag[]
  }) => {
    const formRef = useRef<HTMLFormElement>(null)
    const [selectedTagType, setSelectedTagType] = useState<string>("")
    const { tagValuesForType, loadingTagValues, loadTagValues, resetTagValues } = useTagValues(data)
    const [tempTags, dispatchTempTags] = useReducer(enumerationItemReducer, [])

    const availableAutoTagTypes = useMemo(() => {
      const existingCategories = new Set(existingAutoTags.map((at) => at.category))
      return (data.autoTagTypes ?? []).filter((tagType) => !existingCategories.has(tagType)).sort()
    }, [data.autoTagTypes, existingAutoTags])

    const handleTagTypeChange = useCallback(
      (value: string) => {
        setSelectedTagType(value)
        loadTagValues(value)
        dispatchTempTags({ type: "SET", items: [] })
      },
      [loadTagValues],
    )

    const handleFormSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (formRef.current == null) return

        const formData = new FormData(formRef.current)
        const category = formData.get("autoTagCategory") as string
        const display = formData.get("display") as string
        const hideCategory = formData.get("hideCategory") === "on"
        const hideValue = formData.get("hideValue") === "on"

        if (category.length > 0) {
          onAdd({
            category: category,
            display: display ?? "show",
            hideCategory,
            hideValue,
            enumeration: tempTags,
          })

          dispatchTempTags({ type: "SET", items: [] })
          setSelectedTagType("")
          resetTagValues()
          formRef.current?.reset()
        }
      },
      [onAdd, tempTags, resetTagValues],
    )

    const handleAddTag = useCallback(
      (e?: React.MouseEvent) => {
        e?.preventDefault()
        e?.stopPropagation()
        if (formRef.current == null) return

        const formData = new FormData(formRef.current)
        const tagValue = formData.get("tagValue") as string
        const bgColor = formData.get("tagBg") as string
        const textColor = formData.get("tagText") as string

        if (tagValue.length > 0 && tempTags.find((t) => t.value === tagValue) == null) {
          dispatchTempTags({
            type: "ADD",
            item: {
              value: tagValue,
              background: bgColor ?? "#ffffff",
              color: textColor ?? "#000000",
            },
          })
        }
      },
      [tempTags],
    )

    if (availableAutoTagTypes.length === 0) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block">
              <Button
                type="button"
                variant="outline"
                className="cursor-not-allowed border-border bg-muted px-2 text-xs opacity-50 hover:bg-secondary sm:px-3 sm:text-sm"
                disabled
              >
                <PlusIcon className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
                <span className="xs:inline hidden">Add Auto Tag</span>
                <span className="xs:hidden">Add</span>
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>All available auto tags have been added</TooltipContent>
        </Tooltip>
      )
    }

    return (
      <Dialog>
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="border-purple-200 bg-purple-50 px-2 text-xs hover:bg-purple-100 sm:px-3 sm:text-sm"
          >
            <PlusIcon className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
            <span className="xs:inline hidden">Add Auto Tag</span>
            <span className="xs:hidden">Add</span>
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Auto Tag</DialogTitle>
          </DialogHeader>
          <form ref={formRef} onSubmit={handleFormSubmit} className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Auto Tag Type</label>
                <Select name="autoTagCategory" required value={selectedTagType} onValueChange={handleTagTypeChange}>
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue placeholder="Select auto tag type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAutoTagTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium">Display</label>
                <Select name="display" defaultValue="show">
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {displayOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex items-center space-x-2">
                <Checkbox id="hideCategory" name="hideCategory" />
                <label htmlFor="hideCategory" className="text-xs sm:text-sm">
                  Hide Category
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="hideValue" name="hideValue" />
                <label htmlFor="hideValue" className="text-xs sm:text-sm">
                  Hide Value
                </label>
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <h4 className="text-sm font-medium">Tags</h4>

              {selectedTagType.length === 0 ? (
                <div className="rounded border-2 border-dashed border-border py-4 text-center text-muted-foreground">
                  <p className="text-sm">Select an auto tag type above to configure specific tags</p>
                </div>
              ) : loadingTagValues ? (
                <div className="flex items-center justify-center py-4">
                  <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading tags...</span>
                </div>
              ) : tagValuesForType.length === 0 ? (
                <div className="rounded border-2 border-dashed border-border py-4 text-center text-muted-foreground">
                  <p className="text-sm">There are no configured tags for this auto tag type</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Tag</label>
                    <Select name="tagValue">
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select tag..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tagValuesForType.map((tagValue) => (
                          <SelectItem key={tagValue.value} value={tagValue.value}>
                            {tagValue.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">Background Color</label>
                      <input
                        type="color"
                        name="tagBg"
                        defaultValue="#ffffff"
                        className="h-8 w-full cursor-pointer rounded border border-input"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">Text Color</label>
                      <input
                        type="color"
                        name="tagText"
                        defaultValue="#000000"
                        className="h-8 w-full cursor-pointer rounded border border-input"
                      />
                    </div>
                  </div>
                  <Button type="button" onClick={handleAddTag} size="sm" variant="outline" className="w-full">
                    <PlusIcon className="mr-1 h-4 w-4" />
                    Add Tag
                  </Button>
                </div>
              )}

              {tempTags.length > 0 && (
                <div className="space-y-2 rounded border p-3">
                  <h5 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">Added Tags</h5>
                  <div className={`space-y-2 ${tempTags.length > 2 ? "h-[200px] overflow-y-auto" : ""}`}>
                    {tempTags.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between rounded border bg-muted p-2 text-sm sm:p-3"
                      >
                        <div className="flex items-center gap-2 sm:gap-3">
                          <span className="text-xs font-medium sm:text-sm">{item.value}</span>
                          <div className="flex items-center gap-1 sm:gap-2">
                            <div
                              className="h-4 w-4 rounded border sm:h-6 sm:w-6"
                              style={{ backgroundColor: item.background }}
                            />
                            <div
                              className="h-4 w-4 rounded border sm:h-6 sm:w-6"
                              style={{ backgroundColor: item.color }}
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            dispatchTempTags({ type: "REMOVE", index: idx })
                          }}
                          className="h-6 w-6 p-1 sm:h-7 sm:w-7"
                        >
                          <TrashIcon className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-4 border-t pt-4">
              <DialogClose asChild>
                <Button type="button" variant="ghost">
                  Cancel
                </Button>
              </DialogClose>
              <DialogClose asChild>
                <Button type="submit">Add Auto Tag</Button>
              </DialogClose>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    )
  },
)

AddAutoTagDialog.displayName = "AddAutoTagDialog"

const EditAutoTagDialog = React.memo(
  ({
    autoTag,
    onSave,
    open,
    onOpenChange,
    data,
  }: {
    autoTag: AutoTag
    onSave: (updated: AutoTag) => void
    open: boolean
    onOpenChange: (open: boolean) => void
    data: SharedRecordListTabProps["data"]
  }) => {
    const formRef = useRef<HTMLFormElement>(null)
    const { tagValuesForType, loadingTagValues, loadTagValues } = useTagValues(data)
    const [tempTags, dispatchTempTags] = useReducer(enumerationItemReducer, autoTag.enumeration ?? [])

    React.useEffect(() => {
      if (open) {
        loadTagValues(autoTag.category)
      }
    }, [open, autoTag.category, loadTagValues])

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (formRef.current == null) return

        const formData = new FormData(formRef.current)
        const display = formData.get("display") as string

        const updated: AutoTag = {
          ...autoTag,
          display: display ?? "show",
          hideCategory: formData.get("hideCategory") === "on",
          hideValue: formData.get("hideValue") === "on",
          enumeration: tempTags,
        }

        onSave(updated)
        onOpenChange(false)
      },
      [autoTag, tempTags, onSave, onOpenChange],
    )

    const handleAddTag = useCallback(
      (e?: React.MouseEvent) => {
        e?.preventDefault()
        e?.stopPropagation()
        if (formRef.current == null) return

        const formData = new FormData(formRef.current)
        const tagValue = formData.get("tagValue") as string
        const bgColor = formData.get("tagBg") as string
        const textColor = formData.get("tagText") as string

        if (tagValue.length > 0 && tempTags.find((t) => t.value === tagValue) == null) {
          dispatchTempTags({
            type: "ADD",
            item: {
              value: tagValue,
              background: bgColor ?? "#ffffff",
              color: textColor ?? "#000000",
            },
          })
        }
      },
      [tempTags],
    )

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Auto Tag</DialogTitle>
          </DialogHeader>
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Auto Tag Type</label>
                <Input value={autoTag.category ?? ""} readOnly className="mt-1 bg-muted" />
              </div>

              <div>
                <label className="text-sm font-medium">Display</label>
                <Select name="display" defaultValue={autoTag.display ?? "show"}>
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {displayOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex items-center space-x-2">
                <Checkbox id="editHideCategory" name="hideCategory" defaultChecked={autoTag.hideCategory ?? false} />
                <label htmlFor="editHideCategory" className="text-xs sm:text-sm">
                  Hide Category
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="editHideValue" name="hideValue" defaultChecked={autoTag.hideValue ?? false} />
                <label htmlFor="editHideValue" className="text-xs sm:text-sm">
                  Hide Value
                </label>
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <h4 className="text-sm font-medium">Tags</h4>

              {loadingTagValues ? (
                <div className="flex items-center justify-center py-4">
                  <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading tags...</span>
                </div>
              ) : tagValuesForType.length === 0 ? (
                <div className="rounded border-2 border-dashed border-border py-4 text-center text-muted-foreground">
                  <p className="text-sm">There are no configured tags for this auto tag type</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Tag</label>
                    <Select name="tagValue">
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select tag..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tagValuesForType.map((tagValue) => (
                          <SelectItem key={tagValue.value} value={tagValue.value}>
                            {tagValue.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">Background Color</label>
                      <input
                        type="color"
                        name="tagBg"
                        defaultValue="#ffffff"
                        className="h-8 w-full cursor-pointer rounded border border-input"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">Text Color</label>
                      <input
                        type="color"
                        name="tagText"
                        defaultValue="#000000"
                        className="h-8 w-full cursor-pointer rounded border border-input"
                      />
                    </div>
                  </div>
                  <Button type="button" onClick={handleAddTag} size="sm" variant="outline" className="w-full">
                    <PlusIcon className="mr-1 h-4 w-4" />
                    Add Tag
                  </Button>
                </div>
              )}

              {tempTags.length > 0 && (
                <div className="space-y-2 rounded border p-3">
                  <h5 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">Added Tags</h5>
                  <div className={`space-y-2 ${tempTags.length > 2 ? "h-[200px] overflow-y-auto" : ""}`}>
                    {tempTags.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between rounded border bg-muted p-2 text-sm sm:p-3"
                      >
                        <div className="flex items-center gap-2 sm:gap-3">
                          <span className="text-xs font-medium sm:text-sm">{item.value}</span>
                          <div className="flex items-center gap-1 sm:gap-2">
                            <div
                              className="h-4 w-4 rounded border sm:h-6 sm:w-6"
                              style={{ backgroundColor: item.background }}
                            />
                            <div
                              className="h-4 w-4 rounded border sm:h-6 sm:w-6"
                              style={{ backgroundColor: item.color }}
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            dispatchTempTags({ type: "REMOVE", index: idx })
                          }}
                          className="h-6 w-6 p-1 sm:h-7 sm:w-7"
                        >
                          <TrashIcon className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-4 border-t pt-4">
              <DialogClose asChild>
                <Button type="button" variant="ghost">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    )
  },
)

EditAutoTagDialog.displayName = "EditAutoTagDialog"

const TagFormDialog = React.memo(
  ({
    data,
    onAdd,
    existingTags,
    tagConfigType,
  }: {
    data: SharedRecordListTabProps["data"]
    onAdd: (tag: UserTag) => void
    existingTags: UserTag[]
    tagConfigType: TagConfigType
  }) => {
    const formRef = useRef<HTMLFormElement>(null)
    const [selectedTagType, setSelectedTagType] = useState<string>("")
    const { tagValuesForType, loadingTagValues, loadTagValues, resetTagValues } = useTagValues(data)
    const [tempTags, dispatchTempTags] = useReducer(enumerationItemReducer, [])

    const availableTagTypes = useMemo(() => {
      const existingCategories = new Set(existingTags.map((tag) => tag.category))
      return (data.userTagTypes ?? []).filter((tagType) => !existingCategories.has(tagType)).sort()
    }, [data.userTagTypes, existingTags])

    const handleTagTypeChange = useCallback(
      (value: string) => {
        setSelectedTagType(value)
        loadTagValues(value)
        dispatchTempTags({ type: "SET", items: [] })
      },
      [loadTagValues],
    )

    const handleFormSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (formRef.current == null) return

        const formData = new FormData(formRef.current)
        const category = formData.get("tagType") as string
        const display = formData.get("display") as string
        const textColor = formData.get("textColor") as string
        const backgroundColor = formData.get("backgroundColor") as string
        const hideCategory = formData.get("hideCategory") === "on"

        if (category != null && category !== "") {
          const baseTag: UserTag = {
            category: category,
            display: display ?? "show",
            color: textColor ?? "#000000",
            background: backgroundColor ?? "#ffffff",
            hideCategory,
            enumeration: tempTags,
          }

          if (tagConfigType === "brand-ui-config") {
            baseTag.readOnly = formData.get("readOnly") === "on"
          } else if (tagConfigType === "brand-lenses") {
            baseTag.hideValue = formData.get("hideValue") === "on"
          }

          onAdd(baseTag)

          dispatchTempTags({ type: "SET", items: [] })
          setSelectedTagType("")
          resetTagValues()
          formRef.current?.reset()
        }
      },
      [onAdd, tempTags, tagConfigType, resetTagValues],
    )

    const handleAddTag = useCallback(
      (e?: React.MouseEvent) => {
        e?.preventDefault()
        e?.stopPropagation()
        if (formRef.current == null) return

        const formData = new FormData(formRef.current)
        const tagValue = formData.get("tagValue") as string
        const bgColor = formData.get("tagBg") as string
        const textColor = formData.get("tagText") as string

        if (tagValue.length > 0 && tempTags.find((t) => t.value === tagValue) == null) {
          const enumItem: EnumerationItem = {
            value: tagValue,
            background: bgColor ?? "#ffffff",
            color: textColor ?? "#000000",
          }

          if (tagConfigType === "brand-ui-config") {
            enumItem.hideCategory = formData.get("tagHideCategory") === "on"
          }

          dispatchTempTags({ type: "ADD", item: enumItem })
        }
      },
      [tempTags, tagConfigType],
    )

    if (availableTagTypes.length === 0) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block">
              <Button
                type="button"
                variant="outline"
                className="cursor-not-allowed border-border bg-muted px-2 text-xs opacity-50 hover:bg-secondary sm:px-3 sm:text-sm"
                disabled
              >
                <PlusIcon className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
                <span className="xs:inline hidden">Add User Tag</span>
                <span className="xs:hidden">Add</span>
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>All available user tags have been added</TooltipContent>
        </Tooltip>
      )
    }

    return (
      <Dialog>
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="border-info/30 bg-info/10 px-2 text-xs hover:bg-info/20 sm:px-3 sm:text-sm"
          >
            <PlusIcon className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
            <span className="xs:inline hidden">Add User Tag</span>
            <span className="xs:hidden">Add</span>
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add User Tag</DialogTitle>
          </DialogHeader>
          <form ref={formRef} onSubmit={handleFormSubmit} className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Tag Type</label>
                <Select name="tagType" required value={selectedTagType} onValueChange={handleTagTypeChange}>
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue placeholder="Select tag type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTagTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium">Display</label>
                <Select name="display" defaultValue="show">
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {displayOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Text Color</label>
                <input
                  type="color"
                  name="textColor"
                  defaultValue="#000000"
                  className="mt-1 h-10 w-full cursor-pointer rounded border border-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Background Color</label>
                <input
                  type="color"
                  name="backgroundColor"
                  defaultValue="#ffffff"
                  className="mt-1 h-10 w-full cursor-pointer rounded border border-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex items-center space-x-2">
                <Checkbox id="hideCategory" name="hideCategory" />
                <label htmlFor="hideCategory" className="text-xs sm:text-sm">
                  Hide Category
                </label>
              </div>
              {tagConfigType === "brand-ui-config" && (
                <div className="flex items-center space-x-2">
                  <Checkbox id="readOnly" name="readOnly" />
                  <label htmlFor="readOnly" className="text-xs sm:text-sm">
                    Read Only
                  </label>
                </div>
              )}
              {tagConfigType === "brand-lenses" && (
                <div className="flex items-center space-x-2">
                  <Checkbox id="hideValue" name="hideValue" />
                  <label htmlFor="hideValue" className="text-xs sm:text-sm">
                    Hide Value
                  </label>
                </div>
              )}
            </div>

            <div className="space-y-4 border-t pt-4">
              <h4 className="text-sm font-medium">Tags</h4>

              {selectedTagType.length === 0 ? (
                <div className="rounded border-2 border-dashed border-border py-4 text-center text-muted-foreground">
                  <p className="text-sm">Select a tag type above to configure specific tags</p>
                </div>
              ) : loadingTagValues ? (
                <div className="flex items-center justify-center py-4">
                  <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading tags...</span>
                </div>
              ) : tagValuesForType.length === 0 ? (
                <div className="rounded border-2 border-dashed border-border py-4 text-center text-muted-foreground">
                  <p className="text-sm">There are no configured tags for this tag type</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Tag</label>
                    <Select name="tagValue">
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select tag..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tagValuesForType.map((tagValue) => (
                          <SelectItem key={tagValue.value} value={tagValue.value}>
                            {tagValue.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">Background Color</label>
                      <input
                        type="color"
                        name="tagBg"
                        defaultValue="#ffffff"
                        className="h-8 w-full cursor-pointer rounded border border-input"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">Text Color</label>
                      <input
                        type="color"
                        name="tagText"
                        defaultValue="#000000"
                        className="h-8 w-full cursor-pointer rounded border border-input"
                      />
                    </div>
                  </div>

                  {tagConfigType === "brand-ui-config" && (
                    <div className="flex items-center space-x-2">
                      <Checkbox id="tagHideCategory" name="tagHideCategory" />
                      <label htmlFor="tagHideCategory" className="text-xs">
                        Hide Category
                      </label>
                    </div>
                  )}

                  <Button type="button" onClick={handleAddTag} size="sm" variant="outline" className="w-full">
                    <PlusIcon className="mr-1 h-4 w-4" />
                    Add Tag
                  </Button>
                </div>
              )}

              {tempTags.length > 0 && (
                <div className="space-y-2 rounded border p-3">
                  <h5 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">Added Tags</h5>
                  <div className={`space-y-2 ${tempTags.length > 2 ? "h-[200px] overflow-y-auto" : ""}`}>
                    {tempTags.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between rounded border bg-muted p-2 text-sm sm:p-3"
                      >
                        <div className="flex items-center gap-2 sm:gap-3">
                          <span className="text-xs font-medium sm:text-sm">{item.value}</span>
                          <div className="flex items-center gap-1 sm:gap-2">
                            <div
                              className="h-4 w-4 rounded border sm:h-6 sm:w-6"
                              style={{ backgroundColor: item.background }}
                            />
                            <div
                              className="h-4 w-4 rounded border sm:h-6 sm:w-6"
                              style={{ backgroundColor: item.color }}
                            />
                          </div>
                          {tagConfigType === "brand-ui-config" && item.hideCategory && (
                            <Badge variant="secondary" className="text-xs">
                              Hide Cat
                            </Badge>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            dispatchTempTags({ type: "REMOVE", index: idx })
                          }}
                          className="h-6 w-6 p-1 sm:h-7 sm:w-7"
                        >
                          <TrashIcon className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-4 border-t pt-4">
              <DialogClose asChild>
                <Button type="button" variant="ghost">
                  Cancel
                </Button>
              </DialogClose>
              <DialogClose asChild>
                <Button type="submit">Add User Tag</Button>
              </DialogClose>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    )
  },
)

TagFormDialog.displayName = "TagFormDialog"

const UserTagRow = React.memo(
  ({
    tag,
    index,
    data,
    onChange,
    onRemove,
    tagConfigType,
  }: {
    tag: UserTag
    index: number
    data: SharedRecordListTabProps["data"]
    onChange: (tag: UserTag) => void
    onRemove: () => void
    tagConfigType: TagConfigType
  }) => {
    const hasEnumeration = useMemo(() => (tag.enumeration?.length ?? 0) > 0, [tag.enumeration])
    const [isExpanded, setIsExpanded] = useReducer((state: boolean) => !state, false)
    const [editOpen, setEditOpen] = useReducer((state: boolean, action: boolean) => action, false)

    const handleToggleExpanded = useCallback((e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsExpanded()
    }, [])

    const handleEditClick = useCallback((e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setEditOpen(true)
    }, [])

    const handleRemoveClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        onRemove()
      },
      [onRemove],
    )

    return (
      <>
        <div className="space-y-3 rounded-lg border p-3 lg:p-4">
          <div className="flex flex-col space-y-3 lg:flex-row lg:items-center lg:gap-3 lg:space-y-0">
            <span className="order-1 font-mono text-xs text-muted-foreground lg:w-8 lg:shrink-0">#{index + 1}</span>

            <Input
              value={tag.category ?? ""}
              readOnly
              className="order-2 w-full border-border bg-muted text-sm lg:w-40 lg:shrink-0"
            />

            <div className="order-3 flex flex-wrap items-center gap-3 text-xs lg:order-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Display:</span>
                <span className="rounded bg-secondary px-2 py-1">{tag.display ?? "show"}</span>
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-muted-foreground">BG:</span>
                <div className="h-6 w-6 rounded border" style={{ backgroundColor: tag.background ?? "#ffffff" }} />
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-muted-foreground">Text:</span>
                <div className="h-6 w-6 rounded border" style={{ backgroundColor: tag.color ?? "#000000" }} />
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">Hide Cat:</span>
                  <div className="flex h-5 w-6 items-center justify-center rounded border bg-secondary text-xs">
                    {tag.hideCategory ? "Y" : "N"}
                  </div>
                </div>

                {tagConfigType === "brand-lenses" && (
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">Hide Val:</span>
                    <div className="flex h-5 w-6 items-center justify-center rounded border bg-secondary text-xs">
                      {tag.hideValue ? "Y" : "N"}
                    </div>
                  </div>
                )}

                {tagConfigType === "brand-ui-config" && (
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">R/O:</span>
                    <div className="flex h-5 w-6 items-center justify-center rounded border bg-secondary text-xs">
                      {tag.readOnly ? "Y" : "N"}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="order-4 flex items-center gap-2 lg:order-5 lg:ml-auto">
              {hasEnumeration && (
                <Button type="button" onClick={handleToggleExpanded} variant="ghost" size="sm">
                  <span className="mr-1 text-xs">Tags</span>
                  <Badge variant="secondary" className="h-5 px-1 py-0 text-xs">
                    {tag.enumeration?.length}
                  </Badge>
                  {isExpanded ? (
                    <ChevronUpIcon className="ml-1 h-3 w-3" />
                  ) : (
                    <ChevronDownIcon className="ml-1 h-3 w-3" />
                  )}
                </Button>
              )}

              <Button
                type="button"
                onClick={handleEditClick}
                variant="ghost"
                size="sm"
                className="text-info hover:bg-info/10 hover:text-info"
              >
                <PencilIcon className="h-4 w-4" />
              </Button>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent side="right" align="start" className="max-w-sm">
                  <p className="text-sm">Delete tag &quot;{tag.category}&quot;?</p>
                  <div className="flex items-center justify-end gap-3 pt-4">
                    <PopoverClose asChild>
                      <Button type="button">Cancel</Button>
                    </PopoverClose>
                    <Button type="button" variant="ghost" onClick={handleRemoveClick}>
                      Confirm
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {isExpanded && hasEnumeration && (
            <div className="space-y-3 border-t border-border pt-3">
              <h5 className="text-sm font-medium">Tags</h5>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {tag.enumeration!.map((tagItem, tagIndex) => (
                  <div
                    key={tagIndex}
                    className="flex items-center space-x-3 rounded border border-info/20 bg-info/10 p-3"
                  >
                    <span className="w-4 shrink-0 font-mono text-xs text-muted-foreground">#{tagIndex + 1}</span>
                    <div className="min-w-0 flex-1 truncate text-sm font-medium">{tagItem.value}</div>
                    <div
                      className="h-6 w-6 shrink-0 rounded border"
                      style={{ backgroundColor: tagItem.background ?? "#ffffff" }}
                    />
                    <div
                      className="h-6 w-6 shrink-0 rounded border"
                      style={{ backgroundColor: tagItem.color ?? "#000000" }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <EditUserTagDialog
          tag={tag}
          data={data}
          onSave={onChange}
          open={editOpen}
          onOpenChange={setEditOpen}
          tagConfigType={tagConfigType}
        />
      </>
    )
  },
)

UserTagRow.displayName = "UserTagRow"

const EditUserTagDialogForm = React.memo(
  ({
    tag,
    data,
    onSave,
    onOpenChange,
    tagConfigType,
  }: {
    tag: UserTag
    data: SharedRecordListTabProps["data"]
    onSave: (updatedTag: UserTag) => void
    onOpenChange: (open: boolean) => void
    tagConfigType: TagConfigType
  }) => {
    const formRef = useRef<HTMLFormElement>(null)
    const { tagValuesForType, loadingTagValues, loadTagValues } = useTagValues(data)

    const [editedTag] = useReducer((state: UserTag, updates: Partial<UserTag>) => ({ ...state, ...updates }), tag)

    const [tempTags, dispatchTempTags] = useReducer(enumerationItemReducer, tag.enumeration ?? [])

    const availableTagValues = useMemo(() => {
      if (!Array.isArray(tagValuesForType) || !Array.isArray(tempTags)) {
        return []
      }

      const addedTagValues = new Set(tempTags.map((t) => t?.value).filter(Boolean))
      return tagValuesForType.filter(
        (tagValue) => tagValue?.value != null && tagValue.value !== "" && !addedTagValues.has(tagValue.value),
      )
    }, [tagValuesForType, tempTags])

    React.useEffect(() => {
      if (tag.category.length > 0) {
        loadTagValues(tag.category)
      }
    }, [])

    const handleFormSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (formRef.current == null) return

        const formData = new FormData(formRef.current)
        const updatedTag: UserTag = {
          ...editedTag,
          display: (formData.get("display") as string) ?? "show",
          color: (formData.get("textColor") as string) ?? "#000000",
          background: (formData.get("backgroundColor") as string) ?? "#ffffff",
          hideCategory: formData.get("hideCategory") === "on",
          enumeration: tempTags,
        }

        if (tagConfigType === "brand-ui-config") {
          updatedTag.readOnly = formData.get("readOnly") === "on"
        } else if (tagConfigType === "brand-lenses") {
          updatedTag.hideValue = formData.get("hideValue") === "on"
        }

        onSave(updatedTag)
        onOpenChange(false)
      },
      [editedTag, tempTags, onSave, onOpenChange, tagConfigType],
    )

    const handleAddTag = useCallback(
      (e?: React.MouseEvent) => {
        e?.preventDefault()
        e?.stopPropagation()
        if (formRef.current == null) return

        const formData = new FormData(formRef.current)
        const tagValue = formData.get("tagValue") as string
        const bgColor = formData.get("tagBg") as string
        const textColor = formData.get("tagText") as string

        if (tagValue.length > 0 && tempTags.find((t) => t.value === tagValue) == null) {
          const enumItem: EnumerationItem = {
            value: tagValue,
            background: bgColor ?? "#ffffff",
            color: textColor ?? "#000000",
          }

          if (tagConfigType === "brand-ui-config") {
            enumItem.hideCategory = formData.get("tagHideCategory") === "on"
          }

          dispatchTempTags({ type: "ADD", item: enumItem })

          const tagBgInput = formRef.current?.querySelector<HTMLInputElement>('[name="tagBg"]')
          const tagTextInput = formRef.current?.querySelector<HTMLInputElement>('[name="tagText"]')

          if (tagBgInput != null) tagBgInput.value = "#ffffff"
          if (tagTextInput != null) tagTextInput.value = "#000000"
        }
      },
      [tempTags, tagConfigType],
    )

    const handleUpdateEnumItem = useCallback(
      (index: number, field: "value" | "background" | "color" | "hideCategory", value: string | boolean) => {
        if (index < 0 || index >= tempTags.length) return

        dispatchTempTags({ type: "UPDATE_PROPERTY", index, property: field, value: value })
      },
      [tempTags.length],
    )

    const handleRemoveEnumItem = useCallback(
      (index: number, e?: React.MouseEvent) => {
        e?.preventDefault()
        e?.stopPropagation()
        if (index < 0 || index >= tempTags.length) return

        dispatchTempTags({ type: "REMOVE", index })
      },
      [tempTags.length],
    )

    const getSelectOptions = useMemo(() => {
      const options: Array<{ value: string; name?: string }> = []

      if (Array.isArray(availableTagValues)) {
        availableTagValues.forEach((tagValue) => {
          if (tagValue?.value !== "") {
            options.push(tagValue)
          }
        })
      }

      return options
    }, [availableTagValues])

    return (
      <>
        <DialogHeader>
          <DialogTitle>Edit User Tag</DialogTitle>
        </DialogHeader>
        <form ref={formRef} onSubmit={handleFormSubmit} className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium">Tag Type</label>
              <Input value={editedTag.category ?? ""} readOnly className="mt-1 bg-muted" />
            </div>

            <div>
              <label className="text-sm font-medium">Display</label>
              <Select name="display" defaultValue={editedTag.display ?? "show"}>
                <SelectTrigger className="mt-1 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {displayOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium">Text Color</label>
              <input
                type="color"
                name="textColor"
                defaultValue={editedTag.color ?? "#000000"}
                className="mt-1 h-10 w-full cursor-pointer rounded border border-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Background Color</label>
              <input
                type="color"
                name="backgroundColor"
                defaultValue={editedTag.background ?? "#ffffff"}
                className="mt-1 h-10 w-full cursor-pointer rounded border border-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex items-center space-x-2">
              <Checkbox id="editHideCategory" name="hideCategory" defaultChecked={editedTag.hideCategory} />
              <label htmlFor="editHideCategory" className="text-xs sm:text-sm">
                Hide Category
              </label>
            </div>
            {tagConfigType === "brand-ui-config" && (
              <div className="flex items-center space-x-2">
                <Checkbox id="editReadOnly" name="readOnly" defaultChecked={editedTag.readOnly ?? false} />
                <label htmlFor="editReadOnly" className="text-xs sm:text-sm">
                  Read Only
                </label>
              </div>
            )}
            {tagConfigType === "brand-lenses" && (
              <div className="flex items-center space-x-2">
                <Checkbox id="editHideValue" name="hideValue" defaultChecked={tag.hideValue ?? false} />
                <label htmlFor="editHideValue" className="text-xs sm:text-sm">
                  Hide Value
                </label>
              </div>
            )}
          </div>

          <div className="space-y-4 border-t pt-4">
            <h4 className="text-sm font-medium">Tags</h4>

            {loadingTagValues ? (
              <div className="flex items-center justify-center py-4">
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Loading tags...</span>
              </div>
            ) : tagValuesForType.length === 0 ? (
              <div className="rounded border-2 border-dashed border-border py-4 text-center text-muted-foreground">
                <p className="text-sm">There are no configured tags for this tag type</p>
              </div>
            ) : getSelectOptions.length === 0 ? (
              <div className="rounded border-2 border-dashed border-border py-4 text-center text-muted-foreground">
                <p className="text-sm">All available tags have been added</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Tag Value</label>
                  <Select name="tagValue">
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Select tag value..." />
                    </SelectTrigger>
                    <SelectContent>
                      {getSelectOptions.map((tagValue) => (
                        <SelectItem key={tagValue.value} value={tagValue.value}>
                          {tagValue.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Background Color</label>
                    <input
                      type="color"
                      name="tagBg"
                      defaultValue="#ffffff"
                      className="h-8 w-full cursor-pointer rounded border border-input"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Text Color</label>
                    <input
                      type="color"
                      name="tagText"
                      defaultValue="#000000"
                      className="h-8 w-full cursor-pointer rounded border border-input"
                    />
                  </div>
                </div>

                {tagConfigType === "brand-ui-config" && (
                  <div className="flex items-center space-x-2">
                    <Checkbox id="editTagHideCategory" name="tagHideCategory" />
                    <label htmlFor="editTagHideCategory" className="text-xs">
                      Hide Category
                    </label>
                  </div>
                )}

                <Button type="button" onClick={handleAddTag} size="sm" variant="outline" className="w-full">
                  <PlusIcon className="mr-1 h-4 w-4" />
                  Add Tag Value
                </Button>
              </div>
            )}

            {Array.isArray(tempTags) && tempTags.length > 0 && (
              <div className="space-y-2 rounded border p-3">
                <h5 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">Tags</h5>
                <div className={`space-y-2 ${tempTags.length > 2 ? "h-[200px] overflow-y-auto" : ""}`}>
                  {tempTags.map((item, idx) => (
                    <div
                      key={`${item.value}-${idx}`}
                      className="flex items-center justify-between rounded border bg-muted p-2 text-sm sm:p-3"
                    >
                      <div className="flex flex-1 items-center gap-2 sm:gap-3">
                        <Select
                          value={item.value ?? ""}
                          onValueChange={(value) => handleUpdateEnumItem(idx, "value", value)}
                        >
                          <SelectTrigger className="h-8 min-w-0 flex-1 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {item.value !== "" && <SelectItem value={item.value}>{item.value}</SelectItem>}
                            {getSelectOptions.map((tagValue) => (
                              <SelectItem key={tagValue.value} value={tagValue.value}>
                                {tagValue.value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-1 sm:gap-2">
                          <input
                            type="color"
                            value={item.background ?? "#ffffff"}
                            onChange={(e) => handleUpdateEnumItem(idx, "background", e.target.value)}
                            className="h-6 w-6 cursor-pointer rounded border sm:h-8 sm:w-8"
                          />
                          <input
                            type="color"
                            value={item.color ?? "#000000"}
                            onChange={(e) => handleUpdateEnumItem(idx, "color", e.target.value)}
                            className="h-6 w-6 cursor-pointer rounded border sm:h-8 sm:w-8"
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleRemoveEnumItem(idx, e)}
                        className="ml-2 h-6 w-6 p-1 sm:h-7 sm:w-7"
                      >
                        <TrashIcon className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-4 border-t pt-4">
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit">Save Changes</Button>
          </div>
        </form>
      </>
    )
  },
)

EditUserTagDialogForm.displayName = "EditUserTagDialogForm"

const EditUserTagDialog = React.memo(
  ({
    tag,
    data,
    onSave,
    open,
    onOpenChange,
    tagConfigType,
  }: {
    tag: UserTag
    data: SharedRecordListTabProps["data"]
    onSave: (updatedTag: UserTag) => void
    open: boolean
    onOpenChange: (open: boolean) => void
    tagConfigType: TagConfigType
  }) => {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          {open && (
            <EditUserTagDialogForm
              tag={tag}
              data={data}
              onSave={onSave}
              onOpenChange={onOpenChange}
              tagConfigType={tagConfigType}
            />
          )}
        </DialogContent>
      </Dialog>
    )
  },
)

EditUserTagDialog.displayName = "EditUserTagDialog"

const AutoTagRow = React.memo(
  ({
    autoTag,
    index,
    data,
    onChange,
    onRemove,
  }: {
    autoTag: AutoTag
    index: number
    data: SharedRecordListTabProps["data"]
    onChange: (autoTag: AutoTag) => void
    onRemove: () => void
  }) => {
    const hasEnumeration = useMemo(() => (autoTag.enumeration?.length ?? 0) > 0, [autoTag.enumeration])
    const [isExpanded, setIsExpanded] = useReducer((state: boolean) => !state, false)
    const [editOpen, setEditOpen] = useReducer((state: boolean, action: boolean) => action, false)

    const handleToggleExpanded = useCallback((e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsExpanded()
    }, [])

    const handleEditClick = useCallback((e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setEditOpen(true)
    }, [])

    const handleRemoveClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        onRemove()
      },
      [onRemove],
    )

    return (
      <>
        <div className="space-y-3 rounded-lg border p-3 lg:p-4">
          <div className="flex flex-col space-y-3 lg:flex-row lg:items-center lg:gap-3 lg:space-y-0">
            <span className="order-1 font-mono text-xs text-muted-foreground lg:w-8 lg:shrink-0">#{index + 1}</span>

            <Input
              value={autoTag.category ?? ""}
              readOnly
              className="order-2 w-full border-border bg-muted text-sm lg:w-40 lg:shrink-0"
            />

            <div className="order-3 flex flex-wrap items-center gap-3 text-xs lg:order-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Display:</span>
                <span className="rounded bg-secondary px-2 py-1">{autoTag.display ?? "show"}</span>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">Hide Cat:</span>
                  <div className="flex h-5 w-6 items-center justify-center rounded border bg-secondary text-xs">
                    {(autoTag.hideCategory ?? false) ? "Y" : "N"}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">Hide Val:</span>
                  <div className="flex h-5 w-6 items-center justify-center rounded border bg-secondary text-xs">
                    {(autoTag.hideValue ?? false) ? "Y" : "N"}
                  </div>
                </div>
              </div>
            </div>

            <div className="order-4 flex items-center gap-2 lg:order-5 lg:ml-auto">
              {hasEnumeration && (
                <Button type="button" onClick={handleToggleExpanded} variant="ghost" size="sm">
                  <span className="mr-1 text-xs">Tags</span>
                  <Badge variant="secondary" className="h-5 px-1 py-0 text-xs">
                    {autoTag.enumeration?.length}
                  </Badge>
                  {isExpanded ? (
                    <ChevronUpIcon className="ml-1 h-3 w-3" />
                  ) : (
                    <ChevronDownIcon className="ml-1 h-3 w-3" />
                  )}
                </Button>
              )}

              <Button
                type="button"
                onClick={handleEditClick}
                variant="ghost"
                size="sm"
                className="text-info hover:bg-info/10 hover:text-info"
              >
                <PencilIcon className="h-4 w-4" />
              </Button>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent side="right" align="start" className="max-w-sm">
                  <p className="text-sm">Delete auto tag &quot;{autoTag.category}&quot;?</p>
                  <div className="flex items-center justify-end gap-3 pt-4">
                    <PopoverClose asChild>
                      <Button type="button">Cancel</Button>
                    </PopoverClose>
                    <Button type="button" variant="ghost" onClick={handleRemoveClick}>
                      Confirm
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {isExpanded && hasEnumeration && (
            <div className="space-y-3 border-t border-border pt-3">
              <h5 className="text-sm font-medium">Tags</h5>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {autoTag.enumeration!.map((tagItem, tagIndex) => (
                  <div
                    key={tagIndex}
                    className="flex items-center space-x-3 rounded border border-purple-200 bg-purple-50 p-3 dark:border-purple-900 dark:bg-purple-950/40"
                  >
                    <span className="w-4 shrink-0 font-mono text-xs text-muted-foreground">#{tagIndex + 1}</span>
                    <div className="min-w-0 flex-1 truncate text-sm font-medium">{tagItem.value}</div>
                    <div
                      className="h-6 w-6 shrink-0 rounded border"
                      style={{ backgroundColor: tagItem.background ?? "#ffffff" }}
                    />
                    <div
                      className="h-6 w-6 shrink-0 rounded border"
                      style={{ backgroundColor: tagItem.color ?? "#000000" }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <EditAutoTagDialog autoTag={autoTag} data={data} onSave={onChange} open={editOpen} onOpenChange={setEditOpen} />
      </>
    )
  },
)

AutoTagRow.displayName = "AutoTagRow"

const EvidenceCountRow = React.memo(
  ({
    evidenceCount,
    onChange,
    onRemove,
  }: {
    evidenceCount: ExtendedEvidenceCount
    onChange: (evidenceCount: ExtendedEvidenceCount) => void
    onRemove: () => void
  }) => {
    const [editOpen, setEditOpen] = useReducer((state: boolean, action: boolean) => action, false)
    const [isExpanded, setIsExpanded] = useReducer((state: boolean) => !state, false)

    const handleEditClick = useCallback((e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setEditOpen(true)
    }, [])

    const handleToggleExpanded = useCallback((e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsExpanded()
    }, [])

    return (
      <>
        <div className="space-y-2 rounded-lg border p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 overflow-hidden">
              <div className="truncate text-sm font-medium">{evidenceCount.criteriaLabel ?? evidenceCount.name}</div>
            </div>

            <div className="flex flex-shrink-0 items-center gap-1">
              <Badge variant="outline" className="text-xs">
                {evidenceCount.display ?? "show"}
              </Badge>

              <Button type="button" onClick={handleToggleExpanded} variant="ghost" size="sm" className="h-6 w-6 p-0">
                {isExpanded ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />}
              </Button>

              <Button
                type="button"
                onClick={handleEditClick}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-info hover:bg-info/10"
              >
                <PencilIcon className="h-3 w-3" />
              </Button>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onRemove}
                className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
              >
                <TrashIcon className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {isExpanded && (
            <div className="flex flex-wrap items-center gap-2 border-t pt-2 text-xs">
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">Text:</span>
                <div className="h-4 w-4 rounded border" style={{ backgroundColor: evidenceCount.color ?? "#000000" }} />
              </div>

              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">BG:</span>
                <div
                  className="h-4 w-4 rounded border"
                  style={{ backgroundColor: evidenceCount.background ?? "#ffffff" }}
                />
              </div>

              {evidenceCount.iconType != null && (
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">Icon:</span>
                  <Badge variant="outline" className="text-xs">
                    {evidenceCount.iconType}
                  </Badge>
                  {evidenceCount.icon != null && <code className="text-xs">{evidenceCount.icon}</code>}
                </div>
              )}

              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">Name:</span>
                <span className="text-xs">{evidenceCount.showName !== false ? "✓" : "✗"}</span>
              </div>

              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">Value:</span>
                <span className="text-xs">{evidenceCount.showValue !== false ? "✓" : "✗"}</span>
              </div>
            </div>
          )}
        </div>

        <EditEvidenceCountDialog
          evidenceCount={evidenceCount}
          onSave={onChange}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      </>
    )
  },
)

EvidenceCountRow.displayName = "EvidenceCountRow"

export function SharedRecordListTab({
  initialConfig,
  onSave,
  onRemove,
  isFetching,
  currentAction,
  actionResult,
  tagConfigType,
  data,
}: SharedRecordListTabProps) {
  const [userTags, dispatchUserTags] = useReducer(userTagReducer, initialConfig.userTags)
  const [evidenceCounts, dispatchEvidenceCounts] = useReducer(evidenceCountReducer, initialConfig.evidenceCounts)
  const [autoTags, dispatchAutoTags] = useReducer(autoTagReducer, initialConfig.autoTags)
  const [generalSettings, setGeneralSettings] = useState<GeneralSettings>({
    foldersViewEnabled: initialConfig.foldersViewEnabled,
    showSearch: initialConfig.showSearch,
    summariserEnabled: initialConfig.summariserEnabled,
  })

  React.useEffect(() => {
    if (actionResult?.success === true && actionResult.action === "REMOVE_SECTION") {
      dispatchUserTags({ type: "SET", items: [] })
      dispatchEvidenceCounts({ type: "SET", items: [] })
      dispatchAutoTags({ type: "SET", items: [] })
      setGeneralSettings({ foldersViewEnabled: false, showSearch: false, summariserEnabled: false })
    }
  }, [actionResult])

  const handleSave = useCallback(() => {
    onSave({
      userTags,
      evidenceCounts,
      autoTags,
      ...generalSettings,
    })
  }, [onSave, userTags, evidenceCounts, autoTags, generalSettings])

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mx-auto w-full space-y-6">
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-4 xl:gap-6">
              {/* Left Panel */}
              <div className="flex flex-col gap-4 xl:col-span-1">
                {/* General Settings */}
                <div className="rounded-lg border bg-card p-4">
                  <h3 className="mb-4 text-lg font-medium">General Settings</h3>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="foldersViewEnabled"
                        checked={generalSettings.foldersViewEnabled}
                        onCheckedChange={(checked) =>
                          setGeneralSettings((prev) => ({ ...prev, foldersViewEnabled: checked === true }))
                        }
                      />
                      <label htmlFor="foldersViewEnabled" className="cursor-pointer text-sm">
                        Enable Folders View
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="showSearch"
                        checked={generalSettings.showSearch}
                        onCheckedChange={(checked) =>
                          setGeneralSettings((prev) => ({ ...prev, showSearch: checked === true }))
                        }
                      />
                      <label htmlFor="showSearch" className="cursor-pointer text-sm">
                        Show Search
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="summariserEnabled"
                        checked={generalSettings.summariserEnabled}
                        onCheckedChange={(checked) =>
                          setGeneralSettings((prev) => ({ ...prev, summariserEnabled: checked === true }))
                        }
                      />
                      <label htmlFor="summariserEnabled" className="cursor-pointer text-sm">
                        Enable Summariser
                      </label>
                    </div>
                  </div>
                </div>

                {/* Evidence Counts */}
                <div className="flex flex-col rounded-lg border">
                  <div className="border-b p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-medium">Evidence Counts</h4>
                      <div onClick={(e) => e.stopPropagation()}>
                        <AddEvidenceCountDialog
                          data={data}
                          onAdd={(count) => dispatchEvidenceCounts({ type: "ADD", item: count })}
                          existingCounts={evidenceCounts}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="max-h-60 flex-1 space-y-2 overflow-y-auto p-4">
                    {evidenceCounts.length === 0 ? (
                      <div className="rounded border-2 border-dashed border-border py-4 text-center text-sm text-muted-foreground">
                        No evidence counts added
                      </div>
                    ) : (
                      evidenceCounts.map((evidence, index) => (
                        <EvidenceCountRow
                          key={index}
                          evidenceCount={evidence}
                          onChange={(updated) => dispatchEvidenceCounts({ type: "UPDATE", index, item: updated })}
                          onRemove={() => dispatchEvidenceCounts({ type: "REMOVE", index })}
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Right Panel */}
              <div className="flex flex-col gap-4 xl:col-span-3">
                {/* User Tags */}
                <div className="flex flex-col rounded-lg border">
                  <div className="border-b p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium">User Tags</h3>
                      <div onClick={(e) => e.stopPropagation()}>
                        <TagFormDialog
                          data={data}
                          onAdd={(tag) => dispatchUserTags({ type: "ADD", item: tag })}
                          existingTags={userTags}
                          tagConfigType={tagConfigType}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4">
                    {userTags.length === 0 ? (
                      <div className="rounded-lg border-2 border-dashed border-border py-16 text-center text-muted-foreground">
                        <p className="text-lg">No user tags configured</p>
                        <p className="mt-1 text-sm">Click &quot;Add User Tag&quot; to create your first tag</p>
                      </div>
                    ) : (
                      <div className="space-y-4 pr-2">
                        {userTags.map((tag, index) => (
                          <UserTagRow
                            key={`tag-${index}`}
                            tag={tag}
                            index={index}
                            data={data}
                            onChange={(updatedTag) => dispatchUserTags({ type: "UPDATE", index, item: updatedTag })}
                            onRemove={() => dispatchUserTags({ type: "REMOVE", index })}
                            tagConfigType={tagConfigType}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Auto Tags */}
                <div className="flex flex-col rounded-lg border">
                  <div className="border-b p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium">Auto Tags</h3>
                      <div onClick={(e) => e.stopPropagation()}>
                        <AddAutoTagDialog
                          data={data}
                          onAdd={(tag) => dispatchAutoTags({ type: "ADD", item: tag })}
                          existingAutoTags={autoTags}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="max-h-60 overflow-y-auto p-4">
                    {autoTags.length === 0 ? (
                      <div className="rounded border-2 border-dashed border-border py-8 text-center text-muted-foreground">
                        <p className="text-sm">No auto tags configured</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {autoTags.map((autoTag, index) => (
                          <AutoTagRow
                            key={index}
                            autoTag={autoTag}
                            index={index}
                            data={data}
                            onChange={(updated) => dispatchAutoTags({ type: "UPDATE", index, item: updated })}
                            onRemove={() => dispatchAutoTags({ type: "REMOVE", index })}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t p-4">
          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={isFetching} className="flex-1 bg-info hover:bg-info/90">
              {currentAction === "SAVE_SECTION" && isFetching && (
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              <SaveIcon className="h-4 w-4" />
              Save Record List
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 border-red-300 text-red-500 hover:bg-red-50 hover:text-red-700"
                  disabled={isFetching}
                >
                  {currentAction === "REMOVE_SECTION" && isFetching && (
                    <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  <XCircleIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Remove Section</span>
                  <span className="sm:hidden">Remove</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-80">
                <div className="space-y-3">
                  <p className="text-sm font-medium">Remove Record List Configuration?</p>
                  <p className="text-sm text-muted-foreground">
                    This will remove all record list configurations including all user tags, auto tags, evidence counts,
                    and general settings.
                  </p>
                  <div className="flex items-center justify-end gap-3">
                    <PopoverClose asChild>
                      <Button type="button" variant="ghost" size="sm">
                        Cancel
                      </Button>
                    </PopoverClose>
                    <PopoverClose asChild>
                      <Button type="button" variant="destructive" size="sm" onClick={onRemove} disabled={isFetching}>
                        Remove
                      </Button>
                    </PopoverClose>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    </div>
  )
}
